import { useState, type FocusEvent, type MouseEvent } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  ChevronRight,
  ChevronsUpDown,
  Grid3x3,
  KeyRound,
  LockKeyhole,
  LogOut,
  MonitorSmartphone,
  MonitorX,
  Moon,
  Search,
  Sun,
} from 'lucide-react'
import { toast } from 'sonner'
import type { NavItem, SidebarEntry } from '@/constants/navigation'
import { APP_NAME, APP_VERSION } from '@/constants/app'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/hooks/useAuth'
import { useUiStore } from '@/store/ui.store'
import { authService } from '@/modules/auth/services/auth.service'
import { useLockStore } from '@/modules/auth/lock/idle-lock'
import { findTrustedAccount } from '@/lib/trusted-device'
import { fullName, getInitials } from '@/utils/format'
import { RoleBadge } from '@/components/common/StatusBadges'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { splitGroupChildren, type ActiveNav, type NavGroupEntry, type NavItemEntry, type NavSectionView } from './nav-model'
import type { RailFloatApi } from './useNavShell'

/** Company mark (same artwork as the quotation PDF), on a light tile. */
const NAV_LOGO_SRC = `${import.meta.env.BASE_URL}assets/multipack-logo-mark.v1.png`

export function NavLogo() {
  return (
    <span className="ns-logo">
      <img src={NAV_LOGO_SRC} alt="" width={34} height={34} decoding="async" draggable={false} />
    </span>
  )
}

const SEG_OPT =
  'theme-seg-opt h-7 justify-center gap-1.5 rounded-lg px-2 py-0 text-xs font-semibold focus:bg-transparent'

/** Theme switcher (account menu): system theme Arctic | Navy + Light | Dark, as two segmented
 * radio groups. Items keep the menu open so the change is visible; persisted by the UI store. */
function ThemeSwitcher() {
  const palette = useUiStore((s) => s.palette)
  const setPalette = useUiStore((s) => s.setPalette)
  const themeMode = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const keepOpen = (fn: () => void) => (e: Event) => {
    e.preventDefault()
    fn()
  }
  return (
    <div className="space-y-1.5 px-1 py-1.5">
      <div className="theme-seg" role="group" aria-label="Theme">
        <DropdownMenuItem
          role="menuitemradio"
          aria-checked={palette === 'arctic'}
          className={SEG_OPT}
          onSelect={keepOpen(() => setPalette('arctic'))}
        >
          <span className="theme-swatch theme-swatch--arctic" aria-hidden />
          Arctic
        </DropdownMenuItem>
        <DropdownMenuItem
          role="menuitemradio"
          aria-checked={palette === 'navy'}
          className={SEG_OPT}
          onSelect={keepOpen(() => setPalette('navy'))}
        >
          <span className="theme-swatch theme-swatch--navy" aria-hidden />
          Navy
        </DropdownMenuItem>
      </div>
      <div className="theme-seg" role="group" aria-label="Appearance">
        <DropdownMenuItem
          role="menuitemradio"
          aria-checked={themeMode === 'light'}
          className={SEG_OPT}
          onSelect={keepOpen(() => setTheme('light'))}
        >
          <Sun aria-hidden />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem
          role="menuitemradio"
          aria-checked={themeMode === 'dark'}
          className={SEG_OPT}
          onSelect={keepOpen(() => setTheme('dark'))}
        >
          <Moon aria-hidden />
          Dark
        </DropdownMenuItem>
      </div>
    </div>
  )
}

/** Account menu: identity, theme, logout, build. Shared by the sidebar card and the phone avatar. */
export function UserMenuContent({
  side,
  align,
  signOut = 'navigate',
}: {
  side: 'top' | 'right' | 'bottom'
  align: 'start' | 'end'
  /** Desktop shows a toast and goes to login; phone keeps the old app-bar behaviour (logout only). */
  signOut?: 'navigate' | 'none'
}) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const displayName = user ? fullName(user.firstName, user.lastName) : 'User'

  const handleLogout = async (opts?: { forgetDevice?: boolean }) => {
    await logout(opts)
    if (signOut === 'none') return
    toast.success('Signed out successfully')
    navigate(ROUTES.LOGIN)
  }

  return (
    <DropdownMenuContent side={side} align={align} sideOffset={8} className="ns-menu w-60 rounded-xl">
      <DropdownMenuLabel className="space-y-2 p-2 font-normal">
        <p className="truncate text-sm font-medium leading-none">{displayName}</p>
        <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        {user ? <RoleBadge role={user.role} /> : null}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <ThemeSwitcher />
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}>
        <KeyRound className="mr-2 h-4 w-4" />
        Change password
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => navigate(user?.security?.pinSet ? ROUTES.CHANGE_PIN : ROUTES.PIN_SETUP)}
      >
        <Grid3x3 className="mr-2 h-4 w-4" />
        {user?.security?.pinSet ? 'Change PIN' : 'Set up PIN'}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate(ROUTES.MY_DEVICES)}>
        <MonitorSmartphone className="mr-2 h-4 w-4" />
        My devices
      </DropdownMenuItem>
      {user?.security?.idleMinutes || user?.security?.pinSet ? (
        <DropdownMenuItem
          onClick={() => {
            useLockStore.getState().lock()
            void authService.lock('manual').catch(() => undefined)
          }}
        >
          <LockKeyhole className="mr-2 h-4 w-4" />
          Lock screen
          <span className="ml-auto text-[10px] text-muted-foreground">Short break</span>
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => void handleLogout()}>
        <LogOut className="mr-2 h-4 w-4" />
        Logout
      </DropdownMenuItem>
      {user && findTrustedAccount(user.id) ? (
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => void handleLogout({ forgetDevice: true })}
        >
          <MonitorX className="mr-2 h-4 w-4" />
          Log out &amp; forget device
        </DropdownMenuItem>
      ) : null}
      <p className="px-2 pb-1 pt-1.5 text-[10px] text-muted-foreground">Build {APP_VERSION}</p>
    </DropdownMenuContent>
  )
}

/** Round avatar (photo or initials). */
export function NavAvatar() {
  const { user } = useAuth()
  return (
    <span className="ns-av">
      {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : user ? getInitials(user.firstName, user.lastName) : 'U'}
    </span>
  )
}

function UserCard({ collapsed }: { collapsed: boolean }) {
  const { user } = useAuth()
  return (
    <div className="ns-foot">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="ns-row ns-user" aria-label="Profile menu">
            <span className="ns-pill" />
            <NavAvatar />
            <span className="ns-who ns-fade ns-lbl">
              <b>{user ? fullName(user.firstName, user.lastName) : 'User'}</b>
              <span>{user?.role?.replaceAll('_', ' ') ?? ''}</span>
            </span>
            <ChevronsUpDown className="ns-chev ns-fade" />
          </button>
        </DropdownMenuTrigger>
        <UserMenuContent side={collapsed ? 'right' : 'top'} align={collapsed ? 'end' : 'start'} />
      </DropdownMenu>
    </div>
  )
}

/** Desktop / tablet sidebar content (expanded sidebar and collapsed icon rail). */
export function SidebarBody({
  sections,
  active,
  collapsed,
  rail,
  onSearch,
}: {
  sections: NavSectionView[]
  active: ActiveNav
  collapsed: boolean
  rail: RailFloatApi
  onSearch: () => void
}) {
  const navigate = useNavigate()
  // Keys: group id, and `${groupId}/${subId}` for a sub-section (e.g. master-data/products).
  const activeSubKey = active.subId ? `${active.groupId}/${active.subId}` : ''
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    if (active.groupId) init[active.groupId] = true
    if (activeSubKey) init[activeSubKey] = true
    return init
  })
  // Auto-open the group (and sub-section) that owns the active page (render-time adjust, no effect).
  const activeKey = `${active.groupId}|${activeSubKey}`
  const [seenKey, setSeenKey] = useState(activeKey)
  if (seenKey !== activeKey) {
    setSeenKey(activeKey)
    const next = { ...open }
    let changed = false
    for (const k of [active.groupId, activeSubKey]) {
      if (k && !next[k]) {
        next[k] = true
        changed = true
      }
    }
    if (changed) setOpen(next)
  }
  const toggle = (key: string) => setOpen((o) => ({ ...o, [key]: !o[key] }))

  const renderLeaf = (child: NavItem) => (
    <NavLink
      key={child.id}
      to={child.href}
      end={child.href === '/'}
      className="ns-row"
      data-active={active.id === child.id}
      aria-current={active.id === child.id ? 'page' : undefined}
    >
      <span className="ns-pill" />
      <span className="ns-bar" />
      <span className="ns-lbl ns-fade">{child.label}</span>
    </NavLink>
  )

  const hover = (entry: SidebarEntry) =>
    collapsed
      ? {
          onMouseEnter: (e: MouseEvent<HTMLElement>) => rail.open(entry, e.currentTarget),
          onMouseLeave: rail.close,
          onFocus: (e: FocusEvent<HTMLElement>) => rail.open(entry, e.currentTarget),
          onBlur: rail.close,
        }
      : {}

  const renderItem = (item: NavItemEntry) => (
    <NavLink
      key={item.id}
      to={item.href}
      end={item.href === '/'}
      className="ns-row"
      data-active={active.id === item.id}
      aria-current={active.id === item.id ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      onClick={rail.hide}
      {...hover(item)}
    >
      <span className="ns-pill" />
      <span className="ns-bar" />
      <item.icon className="ns-ico" strokeWidth={1.75} />
      <span className="ns-lbl ns-fade">{item.label}</span>
    </NavLink>
  )

  const renderGroup = (group: NavGroupEntry) => {
    const isOpen = Boolean(open[group.id])
    const hasActive = active.groupId === group.id
    const parts = splitGroupChildren(group)
    const Icon = group.icon
    return (
      <div key={group.id}>
        <button
          type="button"
          className="ns-row"
          data-parent-active={hasActive}
          aria-expanded={!collapsed && isOpen}
          aria-label={collapsed ? group.label : undefined}
          onClick={() => {
            if (collapsed) {
              rail.hide()
              navigate(group.children[0]!.href)
              return
            }
            toggle(group.id)
          }}
          {...hover(group)}
        >
          <span className="ns-pill" />
          <span className="ns-bar" />
          <Icon className="ns-ico" strokeWidth={1.75} />
          <span className="ns-lbl ns-fade">{group.label}</span>
          <ChevronRight className="ns-chev ns-fade" strokeWidth={2} />
        </button>
        <div className="ns-body" data-open={isOpen}>
          <div>
            <div className="ns-kids" inert={!isOpen || collapsed ? true : undefined}>
              {parts.flat.map(renderLeaf)}
              {parts.subs.map(({ sub, items }) => {
                const key = `${group.id}/${sub.id}`
                const subOpen = Boolean(open[key])
                const subActive = hasActive && active.subId === sub.id
                return (
                  <div key={key} className="ns-subgrp">
                    <button
                      type="button"
                      className="ns-row ns-sub"
                      data-parent-active={subActive}
                      aria-expanded={subOpen}
                      onClick={() => toggle(key)}
                    >
                      <span className="ns-pill" />
                      <span className="ns-bar" />
                      <span className="ns-lbl ns-fade">{sub.label}</span>
                      <span className="ns-subn ns-fade">{items.length}</span>
                      <ChevronRight className="ns-chev ns-fade" strokeWidth={2} />
                    </button>
                    <div className="ns-body" data-open={subOpen}>
                      <div>
                        <div className="ns-subkids" inert={!subOpen ? true : undefined}>
                          {items.map(renderLeaf)}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="ns-head">
        <NavLogo />
        <div className="ns-ws ns-fade">
          <b>{APP_NAME}</b>
          <span>Enterprise platform</span>
        </div>
      </div>
      <button type="button" className="ns-row ns-search" aria-label="Search pages (Ctrl K)" onClick={onSearch}>
        <span className="ns-pill" />
        <Search className="ns-ico" strokeWidth={1.75} />
        <span className="ns-lbl ns-fade">Search</span>
        <kbd className="ns-fade">Ctrl K</kbd>
      </button>
      <nav className="ns-scroll" aria-label="Main navigation">
        {sections.map((section) => (
          <div key={section.id} className="ns-sec">
            <div className="ns-sec-h">
              <span className="ns-fade">{section.label}</span>
              <i />
            </div>
            <div className="ns-list">
              {section.entries.map((entry) =>
                entry.type === 'group' ? renderGroup(entry) : renderItem(entry),
              )}
            </div>
          </div>
        ))}
      </nav>
      <UserCard collapsed={collapsed} />
    </>
  )
}
